import originalDayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import objectSupport from "dayjs/plugin/objectSupport";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import type { Locale } from "@eroad/types";

/**
 * This is needed to load the locale data into dayjs
 */
// oxlint-disable-next-line no-unassigned-import
import "dayjs/locale/en-gb";
// oxlint-disable-next-line no-unassigned-import
import "dayjs/locale/fr-ca";

/**
 * These are the thresholds used by dayjs to determine the relative time.
 * It tries to mimic the ones used by date-fns.
 *
 * @see https://day.js.org/docs/en/customization/relative-time
 * @see https://github.com/date-fns/date-fns/blob/main/src/formatDistance/index.ts
 */
const relativeTimeThresholds = [
  { d: "second", l: "s", r: 29 },
  { l: "m", r: 89 },
  { d: "minute", l: "mm", r: 44 },
  { l: "h", r: 89 },
  { d: "hour", l: "hh", r: 23 },
  { l: "d", r: 41 },
  { d: "day", l: "dd", r: 29 },
  { l: "M", r: 44 },
  { d: "month", l: "MM", r: 10 },
  { l: "y", r: 17 },
  { d: "year", l: "yy" },
];

/**
 * 1. Syntax: dayjs(DATE).tz("Pacific/Auckland")
 *
 * Description:
 * This method first creates a dayjs object with the specified date and then converts it to the specified timezone.
 * The date is initially interpreted as being in the local timezone or UTC (depending on the input), and then it is converted to the specified timezone.
 *
 * Use case:
 * Use this when you have a date in a known timezone (local or UTC) and you want to convert it to another timezone.
 *
 * Example:
 * dayjs("2023-10-01T12:00:00Z").tz("Pacific/Auckland").format() -> 2023-10-02T01:00:00+13:00
 *
 * 2. Syntax: dayjs.tz(DATE, "Pacific/Auckland")
 *
 * Description:
 * This method creates a dayjs object with the specified date and directly applies the timezone to it.
 * The date is interpreted as being in the specified timezone.
 *
 * Use case:
 * Use this when you want to create a date that is already in the specified timezone.
 * Note the lack of time zone information in the input date.
 *
 * Example:
 * dayjs.tz("2023-10-01T12:00:00", "Pacific/Auckland").format() -> 2023-10-01T12:00:00+13:00
 */
const dayjs = originalDayjs;
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(objectSupport);
dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime, { thresholds: relativeTimeThresholds });
dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * These mus be in sync with the locales in the dayjs imports above and Ant Design locales.
 */
function setupDayjs(locale: Locale, timeZone: string): void {
  function getDayjsLocale(): "en-gb" | "en" | "fr-ca" {
    switch (locale) {
      case "en-AU":
      case "en-NZ":
        return "en-gb";
      case "fr-CA":
        return "fr-ca";
      default:
        return "en";
    }
  }

  dayjs.tz.setDefault(timeZone);
  dayjs.locale(getDayjsLocale());
}

declare global {
  interface Window {
    dayjs: typeof dayjs;
  }
}

export { dayjs, setupDayjs };
export { type Dayjs } from "dayjs";
